import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { CanceledError } from 'axios';
import PaginationParameters from 'common/contracts/pagination-parameters';
import Guid from 'common/values/guid/guid';
import moment from 'moment';
import { useEffect, useState } from 'react';
import NetworkedGroupAPIService from 'users/entities/networked-group/api/networked-group-api-service';
import UpdateNetworkedGroupApiRequest from 'users/entities/networked-group/api/request-contracts/update-networked-group-api-request';
import NetworkedGroup from "users/entities/networked-group/networked-group";
import NetworkedGroupSelector from 'users/entities/networked-group/view/networked-group-selector';
import UserNetworkConnectionProfile from 'users/entities/user-network-connection/user-network-connection-profile';
import { useSession } from 'users/session/session-context';
import UserCoworkerQuery, { UserCoworkerFilterParameters, UserCoworkerOrderParameters } from 'users/values/user-coworker/api/request-contracts/query-user-coworker-api-request';
import UserCoworkerApiService from 'users/values/user-coworker/api/user-coworker-api-service';
import UserCoworker from 'users/values/user-coworker/user-coworker';

const ContentContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down("sm")]: {
      columnCount: 1,
    },
    [theme.breakpoints.up("md")]: {
      columnCount: 2,
    },
    [theme.breakpoints.up("lg")]: {
      columnCount: 3,
    },
    columnFill: "balance",
    columnGap: theme.spacing(4),
    gridColumn: "1 / auto",
    gridRowStart: "2",
    paddingTop: theme.spacing(0.75),
  }));
const StyledTextField = styled(TextField)(
  ({ theme }) => ({
    marginBottom: theme.spacing(2),
    width: "100%",
  }));
const StyledSubmitButton = styled(Button)(
  ({ theme }) => ({
    marginTop: theme.spacing(2),
    width: "50%",
  }));


export interface EditNetworkedGroupProps {
  groupId: Guid;
  onSave: (group?: UpdateNetworkedGroupApiRequest) => void;
}

export default function EditNetworkedGroupComponent(props: Readonly<EditNetworkedGroupProps>) {
  const { groupId, onSave } = props;

  const [group, setGroup] = useState<NetworkedGroup | null>(null);
  const [groupName, setGroupName] = useState<string | undefined>();
  const [description, setDescription] = useState<string | undefined>();
  const [selectedMembers, setSelectedMembers] = useState<Guid[]>([]);
  const [coworkers, setCoworkers] = useState<UserCoworker[]>([]);
  const [networkConnections, setNetworkConnections] = useState<UserNetworkConnectionProfile[]>([]);

  const session = useSession();

  useEffect(() => {
    const abortController = new AbortController();
    setNetworkConnections(session?.networkConnections);
    void loadCoworkers(abortController);
    void loadGroup(abortController);
    return () => {
      abortController.abort();
    };
  }, []);

  async function loadCoworkers(abortController: AbortController) {
    if (!session.user?.companyEntityId) return;
    try {
      const paginationParameters = new PaginationParameters(0, 100);
      const filterParameters = new UserCoworkerFilterParameters();
      const orderParameters = new UserCoworkerOrderParameters();
      const userCoworkerService = new UserCoworkerApiService(session);

      const coworkerQuery = new UserCoworkerQuery(
        filterParameters,
        orderParameters,
        paginationParameters
      );

      const paginatedResponse = await userCoworkerService.getUserCoworkers(
        coworkerQuery,
        abortController
      );

      setCoworkers(paginatedResponse.data);
    }
    catch (error: unknown) {
      if (error instanceof CanceledError) return;
      console.error("Failed to load coworkers:", error);
    }
  }

  async function loadGroup(abortController: AbortController) {
    try {
      const userGroupService = new NetworkedGroupAPIService(session);
      const response = await userGroupService.getNetworkedGroupById(groupId, abortController);
      setGroup(response);
      setGroupName(response.name);
      setDescription(response.description);
      setSelectedMembers(response.memberUserIds);
    } catch (error: unknown) {
      if (error instanceof CanceledError) return;
      console.error("Failed to load group:", error);
    }
  }

  function generateDefaultGroupName(): string {
    if (selectedMembers.length === 0) return "New Group";
    const selectedMemberNames = selectedMembers.map(id => {
      const coworker = coworkers.find(c => c.userId.value === id.value);
      const connection = networkConnections.find(c => c.user.id.value === id.value);
      return coworker?.name || connection?.user.fullName || "Unknown";
    });
    return `Chat with ${selectedMemberNames.join(", ")}`;
  }

  function groupHasChanged(): boolean {
    if (!group) return false;
    const defaultName = generateDefaultGroupName();
    const currentName = groupName || defaultName;
    if (group.name !== currentName) return true;
    if (group.description !== description) {
      return (group.description ?? "") !== (description ?? "");

    }
    return (group.memberUserIds?.length ?? 0) !== selectedMembers.length ||
      !(group.memberUserIds ?? []).every(id => selectedMembers.some(i => i.value === id.value));
  }

  return (
    <div>
      <ContentContainer>
        <StyledTextField
          label="Group Name"
          variant="outlined"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
        />
        <StyledTextField
          label="Description"
          variant="outlined"
          multiline
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <NetworkedGroupSelector
          coworkers={coworkers}
          networkConnections={networkConnections}
          selectedMemberUserIds={selectedMembers}
          onMemberSelected={(id: Guid) => {
            setSelectedMembers((prev) =>
              prev.some(i => i.value === id.value)
                ? prev.filter(memberId => memberId.value !== id.value)
                : [...prev, id]
            );

          }}
        />
      </ContentContainer>
      <StyledSubmitButton
        variant="contained"
        color="primary"
        disabled={!groupHasChanged()}
        onClick={() => {
          if (!group) return;
          const defaultName = generateDefaultGroupName();
          const request = new UpdateNetworkedGroupApiRequest(
            group,
            new NetworkedGroup(
              group.id,
              groupName || defaultName,
              description,
              selectedMembers,
              moment()
            )
          );
          onSave(request);
        }}>
        Update Group
      </StyledSubmitButton>
    </div>
  );
}
